// Copyright (c) Docugami, Inc. All rights reserved.
import { addSpanIdToParagraphMetadata, getDocugamiIdFromPElement, getParagraphMetadataFromPElement } from "./DgParagraphMetadataUtil";
import { DgWordCanvas } from "./DgWordCanvas";
import { dgGetParagraphsChunksTokens } from "./DgGetParagraphsChunksTokens";
import { LcsSettings } from "./LcsSettings";
import { DOMParser } from '@xmldom/xmldom'
import { IDgWordCanvasProxyCallbacks } from "./worker/worker";
import { StringBuilder } from "utils/StringBuilder";
import { DgXmlNames } from "./DgXmlNames";
import { Logger, LoggerCategory } from "utils/Logger";

/*eslint-disable */

// Note: the following const have eslint-disable because they form the whole of the Open-Xml-Sdk for JavaScript API.
// Most modules will use only a subset of the API, but disabling portions of the API leads to friction when writing
// code.  Disabling eslint for these variables removes the error indicating that a variable is assigned a value but
// never used.  If we ever re-write these modules in TypeScript, then the public API will change significantly.

const Enumerable = require("../../../../../utils/linq");
const Ltxml = require('../../../../../utils/ltxml.js');
Ltxml.DOMParser = DOMParser

const openXml = require('../../../../../utils/openxml');

const XAttribute = Ltxml.XAttribute;
const XCData = Ltxml.XCData;
const XComment = Ltxml.XComment;
const XContainer = Ltxml.XContainer;
const XDeclaration = Ltxml.XDeclaration;
const XDocument = Ltxml.XDocument;
const XElement = Ltxml.XElement;
const XName = Ltxml.XName;
const XNamespace = Ltxml.XNamespace;
const XNode = Ltxml.XNode;
const XObject = Ltxml.XObject;
const XProcessingInstruction = Ltxml.XProcessingInstruction;
const XText = Ltxml.XText;
const XEntity = Ltxml.XEntity;
const cast = Ltxml.cast;
const castInt = Ltxml.castInt;

const W = openXml.W;
const W14 = openXml.W14;
const W15 = openXml.W15;
const NN = openXml.NoNamespace;
const wNs = openXml.wNs;
/*eslint-enable */

export async function dgSelectContentBySpanId(dgWordCanvas: DgWordCanvas, proxyCallbacks: IDgWordCanvasProxyCallbacks, spanIdToSelect: string)
{
    Logger.LogTrace(LoggerCategory.AssistedAuthoring, `dgSelectContentBySpanId spanIdToSelect: ${spanIdToSelect}`);
    return await dgSelectOrDeleteContentBySpanIdInternal(dgWordCanvas, proxyCallbacks, spanIdToSelect, false);
}

export async function dgDeleteContentBySpanId(dgWordCanvas: DgWordCanvas, proxyCallbacks: IDgWordCanvasProxyCallbacks, spanIdToDelete: string)
{
    Logger.LogTrace(LoggerCategory.AssistedAuthoring, `dgDeleteContentBySpanId spanIdToSelect: ${spanIdToDelete}`);
    return await dgSelectOrDeleteContentBySpanIdInternal(dgWordCanvas, proxyCallbacks, spanIdToDelete, true);
}

async function dgSelectOrDeleteContentBySpanIdInternal(dgWordCanvas: DgWordCanvas, proxyCallbacks: IDgWordCanvasProxyCallbacks, spanIdToSelect: string, deleteElseSelect: boolean)
{
    // When we call dgGetParagraphsChunksTokens, we pass in spanIdToSelect, so we get back only the paragraphs/chunks/tokens for the span id
    // that we are selecting.  Note that the pct.DgmlSpanId is equal to the spanIdToSelect that was passed in to dgGetParagraphsChunksTokens.

    const paragraphsChunksTokens = dgGetParagraphsChunksTokens(dgWordCanvas, [spanIdToSelect]);
    if (LcsSettings.lcsDebugFalse) {
        Logger.LogTrace(LoggerCategory.AssistedAuthoring, "ParagraphChunksTokens");
        Logger.LogTrace(LoggerCategory.AssistedAuthoring, "=====================");
        const sb = new StringBuilder();
        paragraphsChunksTokens.forEach((pct: any) => sb.appendLine(`Para: ${pct.ParagraphUuid} Dgml: ${pct.DgmlSpanId} Token: >${pct.Token}<`));
        Logger.LogTrace(LoggerCategory.AssistedAuthoring, sb.toString());
    }

    // get the list of tokens
    // get the corresponding list of tokens

    // for each paragraph
    //   get the paragraph uuid
    //   search the document, get a range
    //   expand the range to the first paragraph
    //   formulate a regex based on the tokens
    //   search for the regex, get a range
    //   push the range onto a list of ranges

    // expand the list of ranges into a single range

    // select the range

    const paragraphUuids = Enumerable.from(paragraphsChunksTokens)
        .where((pct: any) => pct.DgmlSpanId === spanIdToSelect)
        .select((pct: any) => pct.ParagraphUuid)
        .distinct()
        .toArray();

    const selectTokensArray = Enumerable.from(paragraphsChunksTokens)
        .where((pct: any) => pct.DgmlSpanId === spanIdToSelect)
        .select((pct: any) => pct.Token)
        .toArray();

    const selectTokens = selectTokensArray.join(' '); // these are the actual tokens that we want to select.

    // get paragraphs with hidden guids.
    // note that this might return multiple paragraphs.
    const OOXML = await proxyCallbacks.getBodyOoxml();
    const root = XElement.parse(OOXML)
        .descendants(W.body)
        .firstOrDefault();

    const paragraphs = root.descendants(W.p)
        .where((para: any) => 
            para.ancestors(W.sdtContent).firstOrDefault() === null && 
            para.ancestors(W.txbxContent).firstOrDefault() === null)

    const matchedParagraphs = Enumerable.from(paragraphs)
        .select((paragraphElement: any, idx: number) => {
            const guidStr = getDocugamiIdFromPElement(paragraphElement) as string;
            if (guidStr !== null && Enumerable.from(paragraphUuids).any((gl: string) => gl === guidStr)) {
                const matchedParagraph = {
                    Guid: guidStr,
                    ParagraphElement: paragraphElement,
                    Index: idx,
                };
                return matchedParagraph;
            }
            return null;
        })
        .where((n: any) => n !== null)
        .toArray();

    if (LcsSettings.lcsDebugFalse) {
        const sb = new StringBuilder();
        sb.appendLine("Matched Paragraphs");
        sb.appendLine("==================");
        matchedParagraphs.forEach((mp: any) => sb.appendLine(`Para: ${mp.Guid} ParagraphElement: ${mp.ParagraphElement} index: >${mp.Index}<`));
        Logger.LogTrace(LoggerCategory.AssistedAuthoring, sb.toString());
    }

    let deletedChunkSpanIds: string[] = [];
    if (deleteElseSelect)
    {
        const chunkToDelete = dgWordCanvas.dgml
            .descendantsAndSelf()
            .firstOrDefault((d: any) => d.attribute(DgXmlNames.spanId)?.value === spanIdToSelect);
        if (!!chunkToDelete)
        {
            deletedChunkSpanIds = chunkToDelete
                .descendantsAndSelf()
                .select((d: any) => d.attribute(DgXmlNames.spanId)?.value)
                .where((d: any) => !!d)
                .distinct()
                .toArray();
        }

        DgWordCanvas.deletedSpanId = spanIdToSelect;
        Logger.LogDebug(LoggerCategory.AssistedAuthoring, `dgDeleteContentBySpanId deletedSpanId: ${DgWordCanvas.deletedSpanId} (should be removed)`);
    }

    // if content in only one paragraph was selected, then indexes will be an array of length 1.
    // if content is in multiple paragraphs, then indexes will be a longer array.
    // if only a portion of a paragraph is selected, or only a portion of the first paragraph is to be selected, then selectTokens will have the
    // appropriate string of tokens in it.

    const indexes = Enumerable.from(matchedParagraphs).select((mp: any) => mp.Index).toArray();
    await proxyCallbacks.selectOrDeleteContentByParaIdxsAndTokens(indexes, false, selectTokens, deleteElseSelect, deletedChunkSpanIds);
}

export async function dgSelectParagraphByParaGuid(dgWordCanvas: DgWordCanvas, proxyCallbacks: IDgWordCanvasProxyCallbacks, paraGuidToSelect: string)
{
    Logger.LogTrace(LoggerCategory.AssistedAuthoring, `dgSelectParagraphByParaGuid - paraGuidToSelect: ${paraGuidToSelect}`);

    await proxyCallbacks.setEatEvents(true);
    Logger.LogTrace(LoggerCategory.AssistedAuthoring, `dgSelectParagraphByParaGuid - setting dgWordCanvas.eatEvents to true`);
    const timeoutToken = setTimeout(()=> {
        async function fireAndForget() {
            await proxyCallbacks.setEatEvents(false);
            // @ts-ignore
            proxies.setEatEvents[Comlink.releaseProxy]();
        }
        fireAndForget();
    } , 7000); // after 7 seconds, do not eat events, even if not reset elsewhere.

    // get paragraphs with hidden guids
    const OOXML = await proxyCallbacks.getBodyOoxml();

    const root = XElement.parse(OOXML);

    const documentElement = root
        .descendants(W.document)
        .firstOrDefault();

    const paragraphs = documentElement
        .descendants(W.p)
        .where((p: any) => p.ancestors(W.txbxContent).firstOrDefault() === null)
        .toArray();

    Logger.LogTrace(LoggerCategory.AssistedAuthoring, `dgSelectParagraphByParaGuid - creating matchedParagraphs`);
    const matchedParagraphs = Enumerable.from(paragraphs)
        .select((paragraphElement: any, idx: number) => {
            const guidStr = getDocugamiIdFromPElement(paragraphElement) as string;
            if (guidStr !== null && guidStr === paraGuidToSelect) {
                const matchedParagraph = {
                    Guid: guidStr,
                    ParagraphElement: paragraphElement,
                    Index: idx,
                };
                return matchedParagraph;
            }
            return null;
        })
        .where((n: any) => n !== null)
        .toArray();

    if (LcsSettings.lcsDebugFalse) {
        const sb = new StringBuilder();
        sb.appendLine("Matched Paragraphs");
        sb.appendLine("==================");
        matchedParagraphs.forEach((mp: any) => sb.appendLine(`Para: ${mp.Guid} ParagraphElement: ${mp.ParagraphElement} index: >${mp.Index}<`));
        Logger.LogTrace(LoggerCategory.AssistedAuthoring, sb);
    }

    if (matchedParagraphs.length > 0) {
        clearTimeout(timeoutToken);

        const index = matchedParagraphs[0].Index;
        await proxyCallbacks.selectOrDeleteContentByParaIdxsAndTokens([index], false, null, false, []);
        await proxyCallbacks.setEatEvents(false);
    }
}

export function removeDeletedSpanIdsFromDocument(root: any, deletedChunkSpanIds: string[]): boolean
{
    const documentElement = root
        .descendants(W.document)
        .firstOrDefault();

    const paragraphs = documentElement
        .descendants(W.p)
        .where((p: any) => p.ancestors(W.txbxContent).firstOrDefault() === null)
        .toArray();

    let needToReplace = false;

    for (let z = 0; z < paragraphs.length; ++z) {

        // then get the stuff from the XML
        let thisParagraphElement = paragraphs[z];
        const paragraphMetadata = getParagraphMetadataFromPElement(thisParagraphElement);  // [guidString, spanType, spanIdsString]
        if (!!paragraphMetadata)
        {
            const spanIdsString = paragraphMetadata.Ids;

            if (!!spanIdsString)
            {
                // delete the span ids from the list of span ids for the paragraph, get the remainder
                const spanIds = spanIdsString.split('|');
                if (spanIds.some((si: any) => deletedChunkSpanIds.some(z => z === si))) {
                    const newSpanIds = Enumerable.from(spanIds)
                        .where((s: string) => !deletedChunkSpanIds.find(element => element === s))
                        .toArray()
                        .join('|')
                        .trim('|');
                    // then change the paragraph markup
                    addSpanIdToParagraphMetadata(thisParagraphElement, newSpanIds);
                    needToReplace = true;
                }
            }
        }
    }
    return needToReplace;
}

